import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Annotation from "~/components/build/Show/components/Annotation";
import { JobAnnotationsEmptyState } from "~/components/build/Show/components/AnnotationsEmptyState";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { allJobAnnotationsQueryOptions } from "~/components/build/Show/lib/queries/allJobAnnotations";
import type { Annotation as AnnotationType } from "~/components/build/Show/lib/types";
import { useReactRouterNavigateHandler } from "~/components/build/Show/lib/useReactRouterNavigateHandler";
import { applySearchParams, withoutDrawerParams } from "~/components/build/Show/lib/urlForView";
import Spinner from "~/components/shared/Spinner";

function useJobUrl(jobUuid: string) {
  const [searchParams] = useSearchParams();
  const base = withoutDrawerParams(searchParams);
  return applySearchParams("", { jobUuid, tab: "output" }, base);
}

export default function JobAnnotations() {
  const { build } = useBuild();
  const navigateClickHandler = useReactRouterNavigateHandler();

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery(
    allJobAnnotationsQueryOptions(build, {
      placeholderData: keepPreviousData,
    }),
  );

  const handleShowMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const paginatedAnnotations = useMemo(() => {
    return (data?.pages || []).flatMap((page) => page.records);
  }, [data?.pages]);

  if (isLoading) {
    return (
      <div className="flex w-full items-center space-x-2">
        <Spinner className="h-4 w-4" />
        <span>Fetching annotations...</span>
      </div>
    );
  }

  const noAnnotations = paginatedAnnotations.length === 0;
  if (noAnnotations) {
    return <JobAnnotationsEmptyState />;
  }

  return (
    <div className="flex w-full flex-1 flex-col" onClick={navigateClickHandler}>
      {paginatedAnnotations.map((annotation) => (
        <JobAnnotationWithLink key={annotation.id} annotation={annotation} />
      ))}

      {hasNextPage && (
        <div className="flex w-full items-center justify-center">
          {isFetchingNextPage ? (
            <div className="space-x-2">
              <Spinner className="h-4 w-4" />
              <span>Fetching annotations...</span>
            </div>
          ) : (
            <button className="btn btn-small btn-depth" onClick={handleShowMore}>
              Show more
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function JobAnnotationWithLink({ annotation }: { annotation: AnnotationType }) {
  const jobUrl = useJobUrl(annotation.jobUuid ?? "");
  const hasJobLink = Boolean(annotation.jobUuid);

  return (
    <div className="relative">
      {hasJobLink && (
        <Link
          to={jobUrl}
          className="text-charcoal-300 hover:text-charcoal-700 absolute right-[17px] top-[17px] z-10 text-xs"
        >
          View job →
        </Link>
      )}
      <Annotation annotation={annotation} />
    </div>
  );
}
